import React from "react";
import {
  FileText,
  DollarSign,
  UserCheck,
  Building,
  BarChart,
  FileCheck,
  CheckCircle,
} from "lucide-react";
import SectionHeading from "../ui/SectionHeading";

const services = [
  {
    id: "taxation",
    title: "Taxation",
    description:
      "Our comprehensive taxation services help individuals and businesses optimize their tax position while ensuring full compliance with local regulations.",
    icon: <FileText size={36} className="text-psa-green-600" />,
    features: [
      "Personal income tax return preparation and filing",
      "Corporate tax planning",
      "Tax advisory and consulting services",
      "Tax audit, appeal and tribunal representation and defense",
      "Expatriate tax services",
      "Tax dispute resolution",
      "Capital gains tax planning",
    ],
    image:
      "https://images.pexels.com/photos/210574/pexels-photo-210574.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  },
  {
    id: "vat",
    title: "Value Added Tax (VAT)",
    description:
      "Navigate the complexities of VAT regulations with our expert guidance and comprehensive VAT management services.",
    icon: <DollarSign size={36} className="text-psa-green-600" />,
    features: [
      "VAT registration and compliance",
      "VAT return preparation and filing",
      "VAT planning and advisory services",
      "VAT audit, appeal and tribunal support",
      "VAT refund claims assistance",
      "Implementation of VAT systems and processes",
      "VAT dispute resolution",
    ],
    image:
      "https://images.pexels.com/photos/106152/euro-coins-currency-money-106152.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  },
  {
    id: "rjsc",
    title: "RJSC Related Services",
    description:
      "Our expertise in Registrar of Joint Stock Companies affairs ensures smooth company formation and ongoing compliance.",
    icon: <Building size={36} className="text-psa-green-600" />,
    features: [
      "Company name clearance and registration",
      "Formation of local company, foreign company, branch office, liaison office and partnership firm",
      "Annual returns filing",
      "Share transfer and share allotment",
      "Share capital increase or decrease procedures",
      "Company structure modifications",
      "Company secretarial services",
    ],
    image:
      "https://images.pexels.com/photos/2058128/pexels-photo-2058128.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  },
  {
    id: "bida",
    title: "BIDA Related Services",
    description:
      "We provide comprehensive assistance with all Bangladesh Investment Development Authority requirements and processes.",
    icon: <UserCheck size={36} className="text-psa-green-600" />,
    features: [
      "Local company, Foreign company, Liaison and branch office registration",
      "Visa recommendation and security clearance",
      "Work permit issue, ammendment and cancellation for foreign nationals",
      "Industrial IRC adhoc",
      "Regulatory reporting assistance",
    ],
    image:
      "https://www.thomas.co/sites/default/files/styles/resource_banner_image/public/2024-05/Personal-Development-Goals.jpg?itok=VVzLsEav",
  },

  {
    id: "accounting",
    title: "Accounting Solutions",
    description:
      "Our professional accounting services ensure accurate financial reporting and informed business decision-making.",
    icon: <BarChart size={36} className="text-psa-green-600" />,
    features: [
      "Bookkeeping and vouching services",
      "Preparation of Financial statement",
      "Management accounting",
      "Payroll processing",
      "Accounts receivable and payable management",
      "Financial analysis and reporting",
      "Budgeting and forecasting",
    ],
    image:
      "https://images.pexels.com/photos/6693661/pexels-photo-6693661.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  },
  {
    id: "company",
    title: "Company Matters & Licenses",
    description:
      "We handle all aspects of company formation, regulatory compliance, and business licensing requirements.",
    icon: <FileCheck size={36} className="text-psa-green-600" />,
    features: [
      "Business entity selection and structure",
      "IRC, ERC and environmental clearance certificate",
      "Trade, Fire and Factory license acquisition",
      "Industry-specific licensing",
      "Regulatory compliance management",
      "Corporate governance advisory",
      "Business restructuring support",
    ],
    image:
      "https://images.pexels.com/photos/4116380/pexels-photo-4116380.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  },
];

const ServicesList = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <SectionHeading
          title="Our Services"
          subtitle="We provide a wide range of professional services to support your business needs."
          center
        />

        <div className="space-y-20 mt-16">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={service.id}
              className={`scroll-mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "lg:grid-flow-dense" : ""
              }`}
            >
              <div className={index % 2 === 1 ? "lg:col-start-2" : ""}>
                <div className="mb-6">{service.icon}</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4 font-display">
                  {service.title}
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  {service.description}
                </p>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    What We Offer:
                  </h3>
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircle
                          size={18}
                          className="text-psa-green-600 mr-3 mt-1 flex-shrink-0"
                        />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div
                className={`relative ${
                  index % 2 === 1 ? "lg:col-start-1" : ""
                }`}
              >
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div
                  className={`absolute -z-10 rounded-lg ${
                    index % 2 === 0
                      ? "top-10 -right-10 w-full h-full bg-psa-gold-100"
                      : "top-10 -left-10 w-full h-full bg-psa-green-100"
                  }`}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesList;
